/**
 * AdminRoute Component
 * Route guard for admin-only pages.
 *
 * Security model (defence-in-depth):
 *  1. Frontend: this component redirects non-admins (UX guard)
 *  2. Backend: every admin API endpoint re-validates the caller's role server-side
 *
 * Non-admins who are authenticated are redirected to /ai-assist (not login).
 * Unauthenticated users are redirected to /login as usual.
 */

import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@shared/features/auth';
import { ReactNode } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';

interface AdminRouteProps {
    children: ReactNode;
}

export function AdminRoute({ children }: AdminRouteProps) {
    const { authenticated, loading, initialized, isAdmin } = useAuth();
    const location = useLocation();

    // Waiting for session restoration
    if (!initialized) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', gap: 2 }}>
                <CircularProgress size={32} sx={{ color: '#04377b' }} />
                <Typography variant="body2" sx={{ color: '#04377b' }}>Restoring session...</Typography>
            </Box>
        );
    }

    // Auth check in progress
    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <CircularProgress size={32} sx={{ color: '#04377b' }} />
            </Box>
        );
    }

    // Not logged in — redirect to login preserving intended URL
    if (!authenticated) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // Logged in but not an admin — redirect home, NOT a 404/error
    if (!isAdmin) {
        return <Navigate to="/ai-assist" replace />;
    }

    return <>{children}</>;
}
